import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentPasteIcon from '@mui/icons-material/ContentPaste'
import DangerousIcon from '@mui/icons-material/Dangerous'
import HelpIcon from '@mui/icons-material/Help'
import WarningIcon from '@mui/icons-material/Warning'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
    Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { getAdjudicationResult } from '../../../services/claimsService'
import {
    AdjudicateClaimResponse,
    generateAdjudicationTextReport,
} from '../../../services/models/adjudication'
import { IReportSegment, ReportStatus } from '../../../services/models/reports'
import CollapsableDataPanel from '../../CollapsableDataPanel'
import { DataTable } from '../../DataTable'
import ServiceLineDialog from './ServiceLineDialog'

interface AdjudicateClaimResultsProps {
    open: boolean
    onSubmit?: () => void
    disableSubmit?: boolean
    onClearResults?: () => void
    disableClear?: boolean
    onClose: () => void
    adjudicatedClaim?: AdjudicateClaimResponse

    resultsId?: string
}

const generateReportDataTableContents = (data?: IReportSegment) => {
    if (!data) return
    const contents: any = [
        {
            key: 'Status',
            value: data.status,
        },
    ]
    if (data.details.length > 0) {
        contents.push({
            key: 'Details',
            value: data.details,
        })
    }
    return contents
}

export const renderTitle = (title: string, status?: ReportStatus) => (
    <Box sx={{ display: 'flex' }}>
        {renderStatusIcon(status)}
        <Typography variant="h6">{title}</Typography>
    </Box>
)

const renderStatusIcon = (status?: ReportStatus) => {
    const sx = { margin: 'auto', marginRight: 1 }
    switch (status) {
        case ReportStatus.SUCCESS:
            return <CheckCircleIcon sx={sx} color="success" />
        case ReportStatus.WARNING:
            return <WarningIcon sx={sx} color="warning" />
        case ReportStatus.ERROR:
            return <DangerousIcon sx={sx} color="error" />
        case ReportStatus.UNKNOWN:
            return <HelpIcon sx={sx} color="info" />
    }
}

const AdjudicateClaimResultsDialog = (props: AdjudicateClaimResultsProps) => {
    const {
        open,
        onSubmit,
        onClose,
        onClearResults,
        disableSubmit,
        disableClear,
        adjudicatedClaim: _adjudicatedClaim,
        resultsId,
    } = props

    const [adjudicatedClaim, setAdjudicatedClaim] = useState(_adjudicatedClaim)
    const report = adjudicatedClaim?.report.report
    const reportSummary = adjudicatedClaim?.report.summary
    const [svcLineDialogOpen, setSvcLineDialogOpen] = useState(false)
    const messageEntry = reportSummary?.message
        ? {
              key: 'Details',
              value: reportSummary?.message,
          }
        : undefined

    useEffect(() => {
        const fetchAdjudicatedClaim = async () => {
            if (!resultsId) return
            const { data: _adjudicatedClaim } = await getAdjudicationResult(resultsId)
            setAdjudicatedClaim(_adjudicatedClaim)
        }
        fetchAdjudicatedClaim()
    }, [])

    // TODO: loading
    if (!adjudicatedClaim) {
        return <></>
    }

    return (
        <>
            <Dialog open={open} maxWidth="xl">
                <Box
                    sx={{
                        display: 'flex',
                        paddingLeft: 2,
                        paddingRight: 2,
                        paddingTop: 2,
                        paddingBottom: 0,
                    }}
                >
                    <Typography variant="h5">Claim Adjudication Results</Typography>
                    <IconButton
                        onClick={() =>
                            adjudicatedClaim?.report &&
                            navigator.clipboard.writeText(
                                generateAdjudicationTextReport(adjudicatedClaim.report),
                            )
                        }
                        sx={{ marginLeft: 'auto', marginTop: 'auto', marginBottom: 'auto' }}
                    >
                        <ContentPasteIcon />
                    </IconButton>
                </Box>
                <DialogContent sx={{ display: 'flex', overflowY: 'clip' }}>
                    <Stack spacing={4} sx={{ overflowY: 'auto' }}>
                        <CollapsableDataPanel
                            title={renderTitle('Summary', reportSummary?.status)}
                            forceOpen={true}
                            content={
                                <DataTable
                                    data={[
                                        {
                                            key: 'Status',
                                            value: reportSummary?.status,
                                        },
                                        messageEntry,
                                        {
                                            key: 'Submitted to Rhiza Network',
                                            value: reportSummary?.submittedToNetwork ? 'Yes' : 'No',
                                        },
                                        {
                                            key: 'Processing Time',
                                            value:
                                                reportSummary?.processingTime !== undefined
                                                    ? `${reportSummary?.processingTime}ms`
                                                    : undefined,
                                        },
                                        {
                                            key: 'Total Charged',
                                            value: reportSummary?.totalCharged?.toString(),
                                        },
                                        {
                                            key: 'Total to Remit',
                                            value: reportSummary?.toRemit?.toString(),
                                        },
                                        {
                                            key: 'Patient Responsibility',
                                            value: reportSummary?.patientResponsibility?.toString(),
                                        },
                                        {
                                            key: 'Report ID',
                                            value: reportSummary?.id,
                                        },
                                    ]}
                                />
                            }
                        />
                        <CollapsableDataPanel
                            title={renderTitle('Preprocessing', report?.preprocessing.status)}
                            content={
                                <DataTable
                                    data={generateReportDataTableContents(report?.preprocessing)}
                                ></DataTable>
                            }
                        />
                        <CollapsableDataPanel
                            title={renderTitle('Eligibility', report?.eligibility.status)}
                            content={
                                <DataTable
                                    data={generateReportDataTableContents(report?.eligibility)}
                                />
                            }
                        />
                        <CollapsableDataPanel
                            title={renderTitle('Contract', report?.contract.status)}
                            content={
                                <DataTable
                                    data={generateReportDataTableContents(report?.contract)}
                                />
                            }
                        />
                        <CollapsableDataPanel
                            title={renderTitle(
                                'Payment and Publication',
                                report?.paymentAndPublication.status,
                            )}
                            content={
                                <DataTable
                                    data={generateReportDataTableContents(
                                        report?.paymentAndPublication,
                                    )}
                                />
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        disableElevation={true}
                        variant="contained"
                        style={{ minWidth: '120px' }}
                        onClick={onSubmit}
                        disabled={disableSubmit || reportSummary?.status === ReportStatus.ERROR}
                    >
                        Submit Processed Claim
                    </Button>
                    <Button
                        disableElevation={true}
                        variant="contained"
                        style={{ minWidth: '120px' }}
                        onClick={onClearResults}
                        disabled={disableClear}
                    >
                        Clear Results
                    </Button>
                    <Button
                        sx={report ? undefined : { display: 'none' }}
                        disableElevation={true}
                        variant="contained"
                        style={{ minWidth: '120px' }}
                        onClick={() => setSvcLineDialogOpen(true)}
                        disabled={!reportSummary?.linesProcessed}
                    >{`View ${reportSummary?.linesProcessed ?? 0} Service Lines`}</Button>
                    <Button
                        disableElevation={true}
                        variant="contained"
                        style={{ minWidth: '120px' }}
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <ServiceLineDialog
                open={svcLineDialogOpen}
                onClose={() => setSvcLineDialogOpen(false)}
                data={adjudicatedClaim}
            />
        </>
    )
}

export default AdjudicateClaimResultsDialog
